import { useLocale } from '@/composables/locale';
import { useAuth } from '@/composables/auth';
import { useCurrentUser } from '@/composables/currentUser';
import { computed, watch } from 'vue';
import { createGlobalState } from '@vueuse/core';
import { useFeatureFlags } from './featureFlags';

export const useAppReady = createGlobalState(() => {
    const { isLocaleReady } = useLocale();
    const { isAuthenticated } = useAuth();
    const { user } = useCurrentUser();
    const { data: featureFlags, error: featureFlagsError } = useFeatureFlags();

    // Tracks if all data requirements are actually met
    const isDataReady = computed(() => {
        if (isAuthenticated.value) {
            const isFeatureFlagsReady = Boolean(
                featureFlags.value || featureFlagsError.value,
            );
            const isUserReady = Boolean(user.value);

            return isLocaleReady.value && isUserReady && isFeatureFlagsReady;
        }

        return isLocaleReady.value;
    });

    // Router guard waits for actual data to be ready
    const untilDataReady = (): Promise<true> => {
        return new Promise((resolve) => {
            if (isDataReady.value) {
                resolve(true);
                return;
            }

            const stopWatcher = watch(
                isDataReady,
                (ready) => {
                    if (ready) {
                        resolve(true);
                        stopWatcher();
                    }
                },
                { immediate: true },
            );
        });
    };

    return { isDataReady, untilDataReady };
});
